"use client";

import {
  faArrowLeft,
  faEnvelopeOpen,
  faEye,
  faEyeSlash,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { AuthShell } from "@/components/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth/client";

const DEFAULT_NEXT = "/chat";

/**
 * Open-redirect guard for the `next` param (H2-770 — it now carries
 * attacker-influenceable deep links like the escalation review link
 * `/approvals/<id>`). Accepts only same-origin relative paths: must start with
 * "/" but not "//" (protocol-relative → off-site), and must not contain
 * backslashes or whitespace/control characters. Anything else falls back to the
 * default landing path. Both router.push and the magic-link callbackURL
 * (`${origin}${next}`) honor protocol-relative URLs, so both consume the
 * sanitized value.
 */
export function safeNextPath(raw: string | null): string {
  if (!raw) return DEFAULT_NEXT;
  if (!raw.startsWith("/") || raw.startsWith("//")) return DEFAULT_NEXT;
  if (/[\\\s\u0000-\u001f\u007f]/.test(raw)) return DEFAULT_NEXT;
  return raw;
}

export function SignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = safeNextPath(searchParams.get("next"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // Generic error only — never reveal whether the account exists (S3).
  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Invalid email or password.");
      return;
    }
    setLoading(true);
    const { error: signInError } = await authClient.signIn.email({
      email,
      password,
    });
    if (signInError) {
      setError("Invalid email or password.");
      setLoading(false);
      return;
    }
    router.push(next);
  };

  // Enumeration-safe: always show the same neutral confirmation, regardless of
  // whether the email has an account.
  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Enter your email to get a sign-in link.");
      return;
    }
    setLoading(true);
    await authClient.signIn.magicLink({
      email: email.trim(),
      callbackURL: `${window.location.origin}${next}`,
    });
    setLoading(false);
    setLinkSent(true);
  };

  if (linkSent) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FontAwesomeIcon
              icon={faEnvelopeOpen}
              className="h-[22px] w-[22px]"
            />
          </div>
          <h1 className="mb-1.5 text-xl font-bold tracking-[-0.3px] text-foreground">
            Check your email
          </h1>
          <p className="text-base leading-[1.5] text-muted-foreground">
            If an account exists, we&apos;ve sent you a sign-in link.
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            Link expires in 24 hours
          </p>
          {/* Subtle divider, then an always-shown access hint (not conditional on
              the address) → guides genuine newcomers without revealing whether an
              account exists. */}
          <div className="my-4 h-px w-full bg-border" />
          <p className="mb-5 text-sm leading-[1.5] text-muted-foreground/80">
            No access yet? Ask your admin to invite you.
          </p>
          <Button
            variant="outline"
            size="lg"
            className="h-9 w-full"
            onClick={() => {
              setLinkSent(false);
              setMagicMode(false);
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="mb-1 text-xl font-bold tracking-[-0.3px] text-foreground">
        Welcome back
      </h1>
      <p className="mb-5 text-base leading-[1.5] text-muted-foreground">
        Sign in to your HOAi workspace.
      </p>

      {error && (
        <div className="mb-[18px] flex items-center gap-2 rounded-sm border border-destructive/25 bg-destructive/10 px-[11px] py-[9px] text-base text-destructive">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="h-[15px] w-[15px]"
          />
          {error}
        </div>
      )}

      <form
        className="flex flex-col gap-[14px]"
        onSubmit={magicMode ? handleMagicLink : handlePasswordSignIn}
      >
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-[13px] font-medium text-foreground"
          >
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            suppressHydrationWarning
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-9"
          />
        </div>

        {!magicMode && (
          <div>
            <label
              htmlFor="password"
              className="mb-1.5 block text-[13px] font-medium text-foreground"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                required
                suppressHydrationWarning
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-9 pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label="Toggle password visibility"
                className="absolute right-1.5 top-1/2 flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-neutral-100 hover:text-foreground"
              >
                <FontAwesomeIcon
                  icon={showPw ? faEyeSlash : faEye}
                  className="h-[15px] w-[15px]"
                />
              </button>
            </div>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          disabled={loading}
          className="mt-1 h-9 w-full"
        >
          {loading ? (
            <>
              <FontAwesomeIcon
                icon={faSpinner}
                className="h-3.5 w-3.5 animate-spin"
              />
              {magicMode ? "Sending…" : "Signing in…"}
            </>
          ) : magicMode ? (
            "Email me a sign-in link"
          ) : (
            "Sign in"
          )}
        </Button>
      </form>

      {/* H2-974 — always visible (outside the password/magic-link toggle) so a
          magic-link-mode user can still find it. */}
      <div className="mt-3 text-center">
        <Link
          href="/forgot-password"
          className="text-sm text-primary hover:underline"
        >
          Forgot password?
        </Link>
      </div>

      <div className="mb-[14px] mt-5 flex items-center gap-3 text-sm text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <span>or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <button
        type="button"
        onClick={() => {
          setMagicMode((m) => !m);
          setError(null);
        }}
        className="w-full rounded-sm p-1.5 text-center text-base text-primary hover:bg-primary/10"
      >
        {magicMode
          ? "Sign in with a password instead"
          : "Email me a sign-in link instead"}
      </button>
    </AuthShell>
  );
}
